import { useQuery } from '@tanstack/react-query';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import ENDPOINTS from '@/api';
import type { HistoryPage } from '@/types/schemas';

async function fetchSolHistory(
  address: string | undefined,
  cursor?: number,
  limit: number = 20
): Promise<HistoryPage> {
  if (!address) {
    return { items: [], has_more: false, next_cursor: null };
  }

  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      ...(cursor && { cursor: cursor.toString() }),
    });

    const response = await fetch(`${ENDPOINTS.solHistory}?${params}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch SOL history');
    }

    const data = await response.json();
    return data.data || { items: [], has_more: false, next_cursor: null };
  } catch (error) {
    console.error('Error fetching SOL history:', error);
    return { items: [], has_more: false, next_cursor: null };
  }
}

export function useHistory(cursor?: number, limit: number = 20) {
  const { address } = useWalletAddress();

  return useQuery({
    queryKey: ['solHistory', address, cursor, limit],
    queryFn: () => fetchSolHistory(address, cursor, limit),
    enabled: !!address, // Enable when user is connected
    staleTime: 30 * 1000, // 30 seconds
    retry: 1,
  });
}
